import { z } from 'zod';

// ─── Bulk Action Status Types ───────────────────────────────────────
// Shared between BFF (cache writes), engine (status updates), and
// frontend (polling response parsing).

export const RowJobStatusEnum = z.enum(['pending', 'processing', 'done', 'failed']);
export type RowJobStatus = z.infer<typeof RowJobStatusEnum>;

export const BulkJobStatusEnum = z.enum(['ACCEPTED', 'PROCESSING', 'COMPLETED', 'FAILED']);
export type BulkJobStatus = z.infer<typeof BulkJobStatusEnum>;

// Phases reported during script generation. The frontend maps each id to a
// localized title/subtitle so the activity card can show what the backend is
// actually doing, instead of a generic "Starting…" spinner.
export const BulkJobPhaseEnum = z.enum([
    'resolveConfig',
    'fetchFlowContext',
    'transformRecoData',
    'getTopicRecommendations',
    'allocateScripts',
    'runLlmAnalysis',
]);
export type BulkJobPhase = z.infer<typeof BulkJobPhaseEnum>;

export const BulkJobSummarySchema = z.object({
    total: z.number().int().nonnegative(),
    done: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
});
export type BulkJobSummary = z.infer<typeof BulkJobSummarySchema>;

export const BulkJobStatusResponseSchema = z.object({
    status: BulkJobStatusEnum,
    items: z.record(z.string(), RowJobStatusEnum).optional(),
    summary: BulkJobSummarySchema.optional(),
    error: z.string().optional(),
    message: z.string().optional(),
    // Progressive generation fields — updated incrementally during PROCESSING
    total: z.number().int().nonnegative().optional(),
    completed: z.number().int().nonnegative().optional(),
    cancelled: z.boolean().optional(),
    // Current phase the worker is in. Only emitted by jobs that publish phase
    // updates (today: script generation). Older jobs / cached responses may
    // omit it — the frontend treats absence as "no phase info".
    phase: BulkJobPhaseEnum.optional(),
    // True after the user has POSTed to /cancel but before the engine has
    // flipped status to COMPLETED. Lets the UI render a "Cancelling…" state
    // that survives page refreshes — the local cancel mutation state can't.
    cancelRequested: z.boolean().optional(),
    // Export-specific fields — only present when status is COMPLETED for an export job
    downloadUrl: z.string().url().optional(),
    // Filename the export was signed with. The client only needs it for
    // downloads the browser names itself (local `data:` URLs).
    fileName: z.string().optional(),
    rowCount: z.number().int().nonnegative().optional(),
    expiresAt: z.string().datetime().optional(),
    // Reference links the engine could not fetch/read during script generation —
    // surfaced so the UI can warn that those scripts came from description alone.
    unreadableUrls: z.array(z.string()).optional(),
});
export type BulkJobStatusResponse = z.infer<typeof BulkJobStatusResponseSchema>;
